<h1 class="main-title">MZIC Platform Privacy Policy</h1>

<h2 class="section-heading">1. Who we are and processing roles</h2>
<p class="text-content">
  Controller: MZIC Brasil S.A. (Brazil) – CNPJ: 60.978.531/0001‑85.
</p>
<p class="text-content">
  Processors/Suppliers (examples): cloud computing services, identity
  verification (KYC), payment methods, analytics, antifraud, email and messaging
  providers, among others. The updated list can be requested through the channel
  below.
</p>
<p class="text-content">Information channel: legal&#64;mzic.io.</p>

<h2 class="section-heading">2. Scope of application</h2>
<p class="text-content">
  This Policy applies to platform users (rights holders, buyers/investors,
  representatives and visitors), as well as leads who interact with our digital
  channels. Specific internal policies may complement this document in labor and
  institutional relations.
</p>
<h2 class="section-heading">3. Data we collect</h2>

<h3 class="subsection-heading">3.1 Account and usage data</h3>
<ul class="content-list">
  <li>
    Account identification (collected according to user type; exemplary list —
    MZIC may request additional data when necessary for the purposes of this
    Policy and/or to comply with legal/regulatory obligations):
    <ul class="sub-list">
      <li>
        Investor (Individual): full name, email, phone, CPF, date of birth,
        residential address, investor qualification, approximate monthly income
        and citizenship.
      </li>
      <li>
        Rights holder – Individual (artist/performer/producer): full name,
        email, phone, CPF, date of birth, residential address and artistic name
        (when applicable).
      </li>
      <li>
        Label/Producer – Legal Entity: company name/trade name, CNPJ, contact
        email and phone, headquarters address and legal representative (name and
        CPF).
      </li>
      <li>
        Publisher – Legal Entity: company name/trade name, CNPJ, contact email
        and phone, headquarters address, legal representative (name and CPF)
        and, when existing, IPI code, CAE code and ECAD code.
      </li>
      <li>In all cases: credentials and access logs.</li>
    </ul>
  </li>
  <li>
    Platform usage data: executed actions, accessed pages/resources, IP address,
    device identifiers, settings and preferences, language, dates and times of
    access.
  </li>
  <li>
    Communications with MZIC (support, tickets, chat, emails) and content you
    post or send to the platform.
  </li>
</ul>

<h3 class="subsection-heading">3.2 Transactional and financial data</h3>
<ul class="content-list">
  <li>
    Marketplace operations history, orders, settlements, royalty distributions,
    receipts, invoices.
  </li>
  <li>
    Payment methods and billing data limited to what is necessary to process
    transactions (payment tokens, PSP identifiers, payment status).
  </li>
</ul>

<h3 class="subsection-heading">
  3.3 Identity verification (KYC/AML) – includes sensitive data
</h3>
<ul class="content-list">
  <li>
    Official document (front/back) and selfie for biometric validation; date of
    birth, CPF (or equivalent), complete address and other elements required by
    law/regulation and fraud prevention.
  </li>
  <li>
    This data may be collected/validated by specialized suppliers (e.g.: KYC
    services), acting as processors on behalf of MZIC.
  </li>
</ul>

<h3 class="subsection-heading">3.4 Cookies and similar technologies</h3>
<ul class="content-list">
  <li>
    Functionality, performance/analytics and advertising/measurement cookies;
    pixels and SDK identifiers in mobile apps. See Section 7 (Cookies) and Annex
    B (Cookie Categories).
  </li>
</ul>
<h3 class="subsection-heading">3.5 Third-party and public data</h3>
<ul class="content-list">
  <li>
    Information provided by partners (e.g.: rights holders,
    distributors/aggregators, PSPs, partner marketplaces) and publicly available
    data necessary to verify ownership/use of works and phonograms.
  </li>
  <li>
    Analytics and session (PostHog). We collect events and interaction metrics
    through PostHog (analytics service provider), which acts as a processor on
    behalf of MZIC. Depending on the configuration, the following may be
    processed: visited pages, clicks, scrolling, session time, device/browser
    identifiers, IP address (with truncation/anonymization when applicable),
    user-agent and session recordings/heat maps (if enabled). This data is used
    for usage measurement, diagnostics and product improvement (see Section 7
    and Annex B) and is also subject to PostHog's terms and Privacy Policy.
    PostHog may process data in Brazil and/or abroad, according to the
    contracted processing location, observing the safeguards of Section 9. When
    required, collection depends on consent, and you can manage your preferences
    in the Cookie Manager.
  </li>
</ul>

<h2 class="section-heading">4. Legal bases</h2>
<p class="text-content">
  We process data based on, as applicable: (a) compliance with legal or
  regulatory obligations (e.g.: KYC/AML, fraud prevention, tax requirements),
  (b) contract execution or preliminary procedures, (c) legitimate interest
  (e.g.: security, service improvement, abuse prevention), (d) consent (e.g.:
  certain cookies/marketing), and (e) regular exercise of rights (judicial or
  administrative). Sensitive data (e.g.: biometry/selfie for verification) is
  processed when necessary to comply with legal or regulatory obligations and
  fraud prevention/security, with adequate safeguards.
</p>

<h2 class="section-heading">5. Purposes and processing activities</h2>
<ul class="content-list">
  <li>
    Provide and operate the platform: create/manage accounts, personalize
    experience, process transactions, display portfolios, distribute royalties,
    issue tax documents/receipts.
  </li>
  <li>
    KYC/AML and security: verify identity, detect and prevent fraud, block
    abusive activities, maintain system integrity, manage risks and compliance.
  </li>
  <li>
    Contract execution and management: relationship with rights holders,
    distributions/splits, accountability, reconciliation and audits.
  </li>
  <li>
    Support and communications: support, transactional and operational
    notifications.
  </li>
  <li>
    Product improvement and analytics: usage metrics, testing, statistics and
    research (with minimization and, when applicable,
    anonymization/pseudonymization).
  </li>
  <li>
    Marketing and advertising: sending communications and using
    cookies/identifiers upon consent and with cancellation/opt-out option.
  </li>
  <li>
    Legal/regulatory compliance and exercise of rights: records, books and
    evidence retention.
  </li>
</ul>
<h2 class="section-heading">6. Public profile and on-chain data</h2>
<p class="text-content">
  Certain elements of your profile (e.g.: artistic name, photo, public handle
  and digital account/smart wallet address associated with the platform) and
  asset inventory may be publicly visible.
</p>
<ul class="content-list">
  <li>
    On-chain addresses and blockchain data have the nature of permanent and
    public records. When you request deletion, it is not possible to delete
    immutable data recorded on public networks; however, we adopt minimization
    and unlinking measures, such as removing internal links between your account
    and public addresses, when permitted.
  </li>
  <li>
    Metadata and identifiers (e.g.: ISRC/ISWC) necessary for identification and
    accountability may be included in public/private databases of third parties
    (e.g.: management societies, aggregators) and follow their own rules.
  </li>
</ul>

<h2 class="section-heading">7. Cookies and similar technologies</h2>
<p class="text-content">
  We use cookies for authentication, security, preferences,
  performance/analytics and advertising/measurement.
</p>
<ul class="content-list">
  <li>
    We implement a banner/preferences console for consent collection where
    required.
  </li>
  <li>
    You can manage cookies in browser settings and through our Cookie Manager;
    blocking may affect functionality.
  </li>
</ul>
<p class="text-content">Categories (summary):</p>
<ul class="content-list">
  <li>Strictly necessary/functional (e.g.: login, session).</li>
  <li>
    Analytical (e.g.: usage metrics; aggregated, with IP truncation when
    feasible).
  </li>
  <li>
    Advertising/measurement (e.g.: personalization/frequency limitation; always
    subject to consent and opt-out).
  </li>
</ul>
<p class="text-content">Details in Annex B.</p>

<h2 class="section-heading">8. Data sharing</h2>
<p class="text-content">We share personal data only as necessary with:</p>
<ul class="content-list">
  <li>
    Service providers/Processors: cloud computing, KYC/AML, payments, antifraud,
    analytics, customer support and messaging.
  </li>
  <li>
    Business partners: rights holders, aggregators/distributors, marketplaces
    and partner institutions, to enable operations and distributions.
  </li>
  <li>
    Authorities and regulators: upon legal/regulatory obligation or order from
    competent authority.
  </li>
  <li>
    Corporate operations: in case of merger, acquisition, reorganization,
    observing data protection safeguards.
  </li>
</ul>
<p class="text-content">
  We enter into contracts with processors providing for confidentiality,
  purposes, security, documented instructions, subprocessors and audit, in
  accordance with LGPD.
</p>
<h2 class="section-heading">9. International transfers</h2>
<p class="text-content">
  Data may be processed and stored in Brazil and the United States, including in
  cloud providers (e.g.: AWS). We apply contractual clauses and adequate
  guarantees, in addition to transfer assessments, to ensure a level of
  protection compatible with LGPD. When applicable, we will adopt additional
  mechanisms (encryption, pseudonymization, access controls, data residency by
  service).
</p>

<h2 class="section-heading">10. Information security</h2>
<p class="text-content">
  We adopt technical and administrative measures proportional to the risk,
  including encryption at rest and in transit when applicable, infrastructure
  hardening, access controls, logging and monitoring, environment segregation,
  backup and disaster recovery. Security incidents that may cause relevant risk
  or damage will be evaluated and, when required, communicated to the competent
  authority and affected data subjects, with proper records.
</p>

<h2 class="section-heading">11. Data retention and deletion</h2>
<p class="text-content">
  We maintain data only for the time necessary to fulfill the purposes and for
  the minimum applicable legal/regulatory periods (e.g.: money laundering
  prevention, accounting/tax, defense in proceedings). Upon completion, we
  perform deletion or anonymization. See Annex A for reference periods by
  category.
</p>
<h2 class="section-heading">12. Data subject rights (LGPD)</h2>
<p class="text-content">You may exercise, under the terms of LGPD:</p>
<ul class="content-list">
  <li>Confirmation of processing and access;</li>
  <li>Correction of incomplete, inaccurate or outdated data;</li>
  <li>Anonymization, blocking or deletion of unnecessary/excessive data;</li>
  <li>Portability, when applicable;</li>
  <li>
    Information about sharing and about the possibility of not consenting;
  </li>
  <li>Withdrawal of consent;</li>
  <li>Review of automated decisions that affect your interests;</li>
  <li>
    Opposition to processing based on legitimate interest, when applicable.
  </li>
</ul>
<p class="text-content">
  To exercise your rights, use the contact channel indicated in Section 1.
</p>

<h2 class="section-heading">13. Children and adolescents</h2>
<p class="text-content">
  The platform is not directed to minors under 18 years old. If we identify a
  minor's account without proper legal basis, we will adopt appropriate measures
  for deletion or regularization.
</p>

<h2 class="section-heading">14. Marketing communications</h2>
<p class="text-content">
  We send marketing communications based on consent or legitimate interest, when
  appropriate, always with a clear opt-out (cancellation) option in the
  emails/messages themselves or through account settings.
</p>

<h2 class="section-heading">15. Changes to this Policy</h2>
<p class="text-content">
  We may update this Policy to reflect legal, regulatory, technological or
  business changes. We will publish the new version with the effective date and,
  when material changes occur, we will provide additional notice by
  email/platform within reasonable time.
</p>

<h2 class="section-heading">16. How to contact us</h2>
<p class="text-content">
  Questions, requests or complaints about this Policy and the processing of
  personal data can be directed to contact by email: adm&#64;mzic.io.
  <br />
  You also have the right to petition to ANPD.
</p>

<h2 class="section-heading">Annex A – Retention table (reference)</h2>
<p class="text-content">
  The periods below are for reference and may be adjusted according to specific
  legal/regulatory requirements, investigations, defense of rights or authority
  orders. Where there is conflict, the applicable legal period prevails.
</p>
<p class="text-content">
  Account and usage Individual/Legal Entity registration (Individual: name,
  email, phone, CPF, date of birth, address; Investor: qualification, income,
  citizenship; Individual Rights holder: artistic name; Legal Entity: company
  name/trade name, CNPJ, headquarters address, legal representative),
  preferences, access/usage logs – for the duration of the relationship + up to
  5 years after termination (civil statute of limitations); security logs may
  have different periods according to internal policy.
</p>
<p class="text-content">
  Suitability and investor profile (applicable to Investors): suitability
  questionnaires/statements, investor qualification, approximate monthly income,
  profile and acceptance of relevant terms – relationship + 5 years (or longer
  period required by regulatory obligation or fraud prevention); acceptance
  records maintained as necessary for proof of compliance.
</p>
<p class="text-content">
  Rights holders and catalogs registration (Individual/Legal Entity): artistic
  name/alias, label/record company name, catalog IDs, works/phonograms links in
  Dashboard – relationship + 5 years; in disputes, until closure + 5 years.
</p>
<p class="text-content">
  Content identifiers and metadata: ISRC/ISWC/UPC, cue sheets, territories,
  delivery/withdrawal status on DSPs, asset IDs/links – as necessary for
  execution and accountability; local copies and reconciliations for up to 5
  years.
</p>
<p class="text-content">
  Digital distribution (operations): releases, windows/territories, DSP
  statements reports, reconciliations and adjustments – as necessary for
  execution and accountability; in case of audit or clawback, until closure + 5
  years.
</p>
<p class="text-content">
  Splits and royalty distribution: participants, percentages, roles (author,
  performer, etc.), prioritization rules (waterfall), change histories and
  authorizations – while content exploitation is in force + 5 years; in
  disputes, until closure + 5 years.
</p>
<p class="text-content">
  Publishers – society codes: IPI code, CAE code, ECAD code, PROs/societies
  affiliations – while affiliation/management is maintained + 5 years; observe
  PROs/societies rules.
</p>
<p class="text-content">
  Transactions and financial: orders, settlements, royalty distributions,
  receipts, invoices; payment methods (payment tokens, PSP IDs),
  status/chargebacks – relationship + 5 years (fiscal/accounting) and according
  to applicable regulatory obligations; data maintained by PSP follows its own
  policies/periods.
</p>
<p class="text-content">
  KYC/AML (includes sensitive): official document (front/back), selfie/biometry,
  validations, complete address and other elements required by law/regulation
  and fraud prevention – relationship + 5 years (or longer when required by
  regulation/AML or investigations); after, deletion or anonymization.
</p>
<p class="text-content">
  Security and antifraud logs: device fingerprints/identifiers, login attempts,
  suspicious usage patterns, risk scores, abuse flags – 6 to 24 months according
  to technical/security needs; in case of incidents, litigation or legal orders,
  until closure + 5 years.
</p>
<p class="text-content">
  Support and communications: tickets, emails, chat messages, recordings –
  relationship + 2 years (except need for retention for defense of rights).
</p>
<p class="text-content">
  Marketing: preferences, consent/opt-out history, campaign interactions – until
  consent withdrawal + 6 months for proof records.
</p>
<p class="text-content">
  Cookies/analytics: identifiers, navigation/app metrics, telemetry – according
  to technical cycle (generally up to 13 months) and Cookie Manager settings.
</p>
<p class="text-content">
  On-chain data and public profile: wallet/digital account addresses, public
  asset inventory, associated identifiers and on-chain metadata – on-chain
  records are permanent on the public network; MZIC maintains internal links for
  the duration of the relationship + 5 years; after, seeks to unlink/anonymize
  mappings.
</p>
<p class="text-content">
  Third-party and public data: information from PROs/management societies,
  aggregators/distributors, partner marketplaces; registrations/IDs like
  ISRC/ISWC – as necessary for execution and accountability; local copies and
  reconciliations for up to 5 years; observing source terms and contractual
  requirements.
</p>
<p class="text-content">
  Compliance and audit: monitoring reports (AML), control evidence, audits,
  communications to authorities/regulators – up to 10 years when required by
  regulation/audit; in the absence of specific requirement, 5 years.
</p>

<h2 class="section-heading">Annex B – Cookie categories</h2>
<p class="text-content">
  This annex comprehensively describes the categories of cookies and similar
  technologies that may be used by the platform. Not all categories will be
  active at all times, and some depend on your consent.
</p>
<ul class="content-list">
  <li>
    Strictly necessary/functional: Authentication, session maintenance, load
    balancing, routing, basic security (CSRF), privacy preference registration
    (consent cookie) – Typical examples: session_id, csrf_token, load balancer
    cookie, consent preference cookie – Legal basis: Contract execution /
    Legitimate interest – Typical duration: Session to 12 months.
  </li>
  <li>
    Security and antifraud: Bot detection, abuse prevention, rate limiting,
    account integrity – Typical examples: bot detection, rate‑limit, device
    fingerprint (non-tracking), challenge cookies – Legal basis: Legitimate
    interest / Legal obligation – Typical duration: 6–24 months.
  </li>
  <li>
    Preferences: Remember language, layout, user settings – Typical examples:
    lang, ui_theme, remember_me – Legal basis: Legitimate interest (or consent
    where required) – Typical duration: 6–12 months.
  </li>
  <li>
    Usage analytics (PostHog): Measure usage and performance, understand flows,
    improve product (with IP truncation/anonymization when applicable) – Typical
    examples: Navigation events, clicks, page time, user-agent – Legal basis:
    Consent (where required) / Legitimate interest – Typical duration: Up to 13
    months.
  </li>
  <li>
    Session recording / heatmaps (PostHog – optional): Session replay and heat
    maps for UX diagnostics; never for reading sensitive fields – Typical
    examples: Session replays, click/scroll areas – Legal basis: Consent –
    Typical duration: Up to 13 months.
  </li>
  <li>
    Experiments/A-B testing and performance: Test interface and feature
    variations, feature flags – Typical examples: exp_variant, feature_flag_* –
    Legal basis: Legitimate interest (or consent where required) – Typical
    duration: 6–12 months.
  </li>
  <li>
    Errors and diagnostics: Monitor errors, crash reports, stability – Typical
    examples: error_id, trace_id – Legal basis: Legitimate interest – Typical
    duration: 3–12 months.
  </li>
  <li>
    Advertising/measurement: Personalization, campaign measurement, frequency
    capping. Activated only when adopted and with consent – Typical examples:
    Advertising/measurement identifiers – Legal basis: Consent – Typical
    duration: 3–13 months.
  </li>
  <li>
    Social media/embeds: Share buttons, players and third-party integrations –
    Typical examples: Cookies set by social media platforms/players – Legal
    basis: Consent – Typical duration: 3–13 months.
  </li>
  <li>
    Tag and consent management: Trigger tags in a controlled manner and respect
    privacy preferences – Typical examples: gtm_*, consent_mode_* – Legal basis:
    Legitimate interest – Typical duration: 12–24 months.
  </li>
  <li>
    Mobile SDKs / notifications: Technical identifiers for telemetry and push
    notifications in the app – Typical examples: Device/installation ID – Legal
    basis: Consent / Legitimate interest – Typical duration: Up to 24 months (or
    app cycle).
  </li>
</ul>
